import { useState, useEffect, useRef, type ReactNode } from "react";
import { useParams, Link, useLocation } from "wouter";
import { usePageTitle } from "@/hooks/use-page-title";
import { emitTourEvent, useGuidedTour } from "@/hooks/use-guided-tour";
import { useSettingsFormat } from "@/hooks/use-settings-format";
import { 
  useGetEstimate,
  useUpdateEstimate,
  useDeleteEstimate,
  useDuplicateEstimate,
  useConvertEstimateToInvoice,
  useSendEstimateEmail,
  useListCustomers,
  useCreateCustomer,
  getGetEstimateQueryKey,
  getGetSettingsQueryKey,
  getListEstimatesQueryKey,
  getListCustomersQueryKey,
  useGetSettings,
  useGetEstimateMilestones,
  useUpsertEstimateMilestones,
  getListEstimateMilestonesQueryKey,
  useCreateLineItemGroup,
  useCreateDepositInvoice,
  getListInvoicesQueryKey,
  type EstimateDetail,
  type EstimateMilestoneInput,
  type EstimateMilestoneItem,
  type LineItemGroup,
} from "@workspace/api-client-react";
import { QuickBooksPushButton } from "@/components/quickbooks-push-button";
import { XeroPushButton } from "@/components/xero-push-button";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { 
  ArrowLeft, Edit, Trash2, Plus, GripVertical, EyeOff, FileText,
  Lock, ChevronDown, ChevronUp, ArrowUp, ArrowDown, Download, Search, Save, Loader2, User, Copy, Eye, DollarSign, Percent, Circle, Receipt, RefreshCw, Mail, Check, PlusCircle, Group, X, AlertTriangle, HelpCircle, Pencil
} from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { InfoTip } from "@/components/ui/info-tip";
import { FORMULAS } from "@/lib/formulas";
import { PaymentPlanPanel, type PaymentMode } from "@/components/payment-plan-panel";
import { exportCustomerPDF, exportInternalPDF, previewCustomerPDF, previewInternalPDF, customerPDFBase64, customerPDFFilename } from "@/lib/pdf";
import { PDFPreviewDialog } from "@/components/pdf-preview-dialog";
import { AttachmentsPanel } from "@/components/attachments-panel";
import { ProjectAssignment } from "@/components/project-assignment";
import { ProGate } from "@/components/pro-gate";
import { useAuth, getTrialInfo } from "@/hooks/use-auth";
import { EditorActionBar } from "@/components/editor-action-bar";
import type { ProFeatureKey } from "@workspace/pricing";
import { PRO_FEATURES } from "@workspace/pricing";
import { MarkAsSentDialog } from "@/components/mark-as-sent-dialog";
import { DepositInvoicePromptDialog } from "@/components/deposit-invoice-prompt-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { InlineEditField } from "@/components/inline-edit-field";
import { InlineEditDate } from "@/components/inline-edit-date";
import { InlineCustomerSelect } from "@/components/inline-customer-select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CreateCustomerModal, type CreateCustomerFormValues } from "@/components/create-customer-modal";
import { EditorDisclaimer } from "@/components/editor-disclaimer";
import { InternalWorkshopView, breakdownFromMaterials } from "@/components/internal-workshop-view";
import { useTargetMargin } from "@/hooks/use-target-margin";
import { GROUP_ACCENT_COLORS, AddItemSearch, GroupHeader, LineItemRow } from "@/components/estimate-line-items";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { computeDocumentTotals, parseSelectedTaxLines, reconcileTaxLineIds, type TaxLine } from "@workspace/totals";

const GATE_RECURRING: ProFeatureKey = "RECURRING_INVOICES";
const GATE_MILESTONE: ProFeatureKey = "PROGRESS_PAYMENTS";
const GATE_COST_ADD: ProFeatureKey = "COST_CATALOGUE_ADD";

const updateEstimateSchema = z.object({
  customerId: z.number().optional(),
  estimateNumber: z.string().optional(),
  title: z.string().min(1),
  description: z.string().optional(),
  termsAndNotes: z.string().nullable().optional(),
  estimateDate: z.string().optional(),
  validUntil: z.string().optional(),
});



type InternalWorkshopCardProps = {
  estimate: {
    profit?: number | null;
    margin?: number | null;
    lineItems: { materials?: { type: string | null | undefined; totalCost: number }[] }[];
    internalCost?: number | null;
    targetMargin?: number | null;
  };
  formatCurrency: (value: number) => string;
  className?: string;
  onTargetChange?: (target: number | null) => void;
};

function InternalWorkshopCard({ estimate, formatCurrency, className, onTargetChange }: InternalWorkshopCardProps) {
  const { target, defaultTarget } = useTargetMargin(estimate.targetMargin);
  const allMats = estimate.lineItems.flatMap(li => li.materials ?? []);
  const breakdown = allMats.length > 0 ? breakdownFromMaterials(allMats) : null;
  return (
    <InternalWorkshopView
      profit={estimate.profit}
      margin={estimate.margin}
      internalCost={estimate.internalCost}
      breakdown={breakdown}
      formatCurrency={formatCurrency}
      className={className}
      target={target}
      defaultTarget={defaultTarget}
      ownTarget={estimate.targetMargin != null}
      onTargetChange={onTargetChange}
    />
  );
}

export default function EstimateDetail() {
  const params = useParams();
  const estimateId = parseInt(params.id || "0", 10);
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { hasProAccess: isPro } = getTrialInfo(user);

  const [isDeleteDialogOpen, setIsDeleteOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  const [previewTitle, setPreviewTitle] = useState("");
  const [markAsSentOpen, setMarkAsSentOpen] = useState(false);
  const [depositPromptOpen, setDepositPromptOpen] = useState(false);
  const [convertConfirmOpen, setConvertConfirmOpen] = useState(false);
  const [sendEmailOpen, setSendEmailOpen] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [ccSelf, setCcSelf] = useState(true);
  const [attachServiceAgreement, setAttachServiceAgreement] = useState(true);
  const [saveAsTemplateOpen, setSaveAsTemplateOpen] = useState(false);
  const [saveAsTemplateName, setSaveAsTemplateName] = useState("");
  const [saveAsTemplateDesc, setSaveAsTemplateDesc] = useState("");
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [addLineItemOpen, setAddLineItemOpen] = useState(false);
  const [addingToGroupId, setAddingToGroupId] = useState<number | null>(null);
  // True while synchronously generating the PDF attachment before sending email.
  const [isGeneratingEmailPdf, setIsGeneratingEmailPdf] = useState(false);

  // Reads the current walkthrough step; only the overlay acts on tour events.
  const { step: tourStep } = useGuidedTour();

  // Auto-advance when PDF preview dialog closes.
  const prevPreviewOpenRef = useRef(false);
  useEffect(() => {
    if (prevPreviewOpenRef.current && !previewOpen && tourStep?.id === "preview-pdf") {
      emitTourEvent("quote.preview-closed");
    }
    prevPreviewOpenRef.current = previewOpen;
  }, [previewOpen, tourStep?.id]);

  const { data: estimate, isLoading } = useGetEstimate(estimateId, {
    query: { enabled: !!estimateId, queryKey: getGetEstimateQueryKey(estimateId) }
  });
  usePageTitle(estimate?.title ?? "Quote");

  const { data: companySettings } = useGetSettings({
    query: { queryKey: getGetSettingsQueryKey() }
  });

  const { data: estimateMilestones } = useGetEstimateMilestones(estimateId, {
    query: { queryKey: getListEstimateMilestonesQueryKey(estimateId), enabled: !!estimateId },
  });

  const estimateForPdf = (): EstimateDetail & { milestones?: typeof estimateMilestones } => ({
    ...estimate!,
    milestones: estimateMilestones ?? [],
  });

  const updateEstimate = useUpdateEstimate();
  const deleteEstimate = useDeleteEstimate();
  const duplicateEstimate = useDuplicateEstimate();
  const convertToInvoice = useConvertEstimateToInvoice();
  const createDepositInvoice = useCreateDepositInvoice();
  const sendEmailMutation = useSendEstimateEmail();
  const createCustomer = useCreateCustomer();
  const createGroup = useCreateLineItemGroup();

  const handleAddGroup = () => {
    if (!estimateId) return;
    const groups = estimate?.groups ?? [];
    const maxSort = groups.reduce((m, g) => Math.max(m, g.sortOrder ?? 0), -1);
    const accentColor = GROUP_ACCENT_COLORS[groups.length % GROUP_ACCENT_COLORS.length].hex;
    createGroup.mutate(
      { estimateId, data: { name: "New Group", sortOrder: maxSort + 1, accentColor } },
      {
        onSuccess: () => queryClient.invalidateQueries({ queryKey: getGetEstimateQueryKey(estimateId) }),
        onError: () => toast({ title: "Failed to add group", variant: "destructive" }),
      }
    );
  };

  const { data: customers, isLoading: isLoadingCustomers } = useListCustomers();

  const [createCustomerModalOpen, setCreateCustomerModalOpen] = useState(false);
  const [pendingCustomerName, setPendingCustomerName] = useState("");

  const [inlineCustomerOpen, setInlineCustomerOpen] = useState(false);
  const [inlineCustomerSearch, setInlineCustomerSearch] = useState("");

  // A target of null clears the quote's own target, so it uses the Settings default.
  const saveTargetMargin = (targetMargin: number | null) => {
    updateEstimate.mutate({ id: estimateId, data: { targetMargin } }, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetEstimateQueryKey(estimateId) });
        queryClient.invalidateQueries({ queryKey: getListEstimatesQueryKey() });
      },
      onError: () => toast({ title: "Failed to save the target", variant: "destructive" }),
    });
  };

  const saveEstimateField = (field: Partial<z.infer<typeof updateEstimateSchema>>) => {
    updateEstimate.mutate({ id: estimateId, data: field }, {
      onSuccess: () => queryClient.invalidateQueries({ queryKey: getGetEstimateQueryKey(estimateId) }),
      onError: () => toast({ title: "Failed to save", variant: "destructive" }),
    });
  };

  const handleCreateCustomerFromInline = (data: CreateCustomerFormValues) => {
    createCustomer.mutate(
      { data: { name: data.name, email: data.email || undefined, phone: data.phone || undefined, addressLine1: data.addressLine1 || undefined } },
      {
        onSuccess: (newCustomer) => {
          queryClient.invalidateQueries({ queryKey: getListCustomersQueryKey() });
          setCreateCustomerModalOpen(false);
          setInlineCustomerOpen(false);
          setInlineCustomerSearch("");
          updateEstimate.mutate({ id: estimateId, data: { customerId: newCustomer.id } }, {
            onSuccess: () => {
              queryClient.invalidateQueries({ queryKey: getGetEstimateQueryKey(estimateId) });
              toast({ title: "Customer updated" });
            },
            onError: () => {
              toast({ title: "Failed to assign customer to quote", variant: "destructive" });
            },
          });
        },
        onError: () => {
          toast({ title: "Failed to create customer", variant: "destructive" });
        },
      }
    );
  };

  const handleInlineCustomerChange = (customerId: number) => {
    if (customerId === estimate?.customerId) {
      setInlineCustomerOpen(false);
      setInlineCustomerSearch("");
      return;
    }
    updateEstimate.mutate({ id: estimateId, data: { customerId } }, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetEstimateQueryKey(estimateId) });
        setInlineCustomerOpen(false);
        setInlineCustomerSearch("");
        toast({ title: "Customer updated" });
      },
      onError: () => {
        toast({ title: "Failed to update customer", variant: "destructive" });
      },
    });
  };

  const onStatusChange = (status: "draft" | "sent" | "accepted" | "declined") => {
    updateEstimate.mutate({ id: estimateId, data: { status } }, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetEstimateQueryKey(estimateId) });
        queryClient.invalidateQueries({ queryKey: getListEstimatesQueryKey() });
        toast({ title: `Status updated to ${status}` });
        if (status === "sent") emitTourEvent("quote.sent");
        if (status === "accepted" && (estimate?.depositValue ?? 0) > 0) {
          setDepositPromptOpen(true);
        }
      }
    });
  };

  const handleDepositInvoiceConfirm = () => {
    convertToInvoice.mutate({ id: estimateId }, {
      onSuccess: (invoice) => {
        queryClient.invalidateQueries({ queryKey: getGetEstimateQueryKey(estimateId) });
        queryClient.invalidateQueries({ queryKey: getListEstimatesQueryKey() });
        createDepositInvoice.mutate({ parentInvoiceId: invoice.id }, {
          onSuccess: (depositInvoice) => {
            queryClient.invalidateQueries({ queryKey: getListInvoicesQueryKey() });
            setDepositPromptOpen(false);
            toast({ title: "Deposit invoice created — opening it now" });
            emitTourEvent("invoice.created", { invoiceId: depositInvoice.id });
            setLocation(`/invoices/${depositInvoice.id}`);
          },
          onError: () => {
            setDepositPromptOpen(false);
            toast({ title: "Invoice created but deposit invoice failed", variant: "destructive" });
            setLocation(`/invoices/${invoice.id}`);
          },
        });
      },
      onError: () => {
        setDepositPromptOpen(false);
        toast({ title: "Failed to convert quote to invoice", variant: "destructive" });
      },
    });
  };

  const onDelete = () => {
    deleteEstimate.mutate({ id: estimateId }, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListEstimatesQueryKey() });
        toast({ title: "Quote deleted" });
        setLocation("/quotes");
      }
    });
  };

  const onDuplicate = () => {
    duplicateEstimate.mutate({ id: estimateId }, {
      onSuccess: (result) => {
        queryClient.invalidateQueries({ queryKey: getListEstimatesQueryKey() });
        toast({ title: "Quote duplicated" });
        setLocation(`/quotes/${result.id}`);
      },
      onError: () => {
        toast({ title: "Failed to duplicate quote", variant: "destructive" });
      }
    });
  };

  const doConvertToInvoice = () => {
    convertToInvoice.mutate({ id: estimateId }, {
      onSuccess: (result) => {
        queryClient.invalidateQueries({ queryKey: getGetEstimateQueryKey(estimateId) });
        queryClient.invalidateQueries({ queryKey: getListEstimatesQueryKey() });
        toast({ title: "Invoice created from quote" });
        emitTourEvent("invoice.created", { invoiceId: result.id });
        setLocation(`/invoices/${result.id}`);
      },
      onError: () => {
        toast({ title: "Failed to convert quote to invoice", variant: "destructive" });
      }
    });
  };

  const onConvertToInvoice = () => {
    if (estimate?.status === "accepted") {
      doConvertToInvoice();
    } else {
      setConvertConfirmOpen(true);
    }
  };

  const { formatCurrency, formatDate } = useSettingsFormat();

  const depositAmountForDialog = (() => {
    if (!estimate) return 0;
    const dv = estimate.depositValue ?? 0;
    if (dv <= 0) return 0;
    if ((estimate.depositType ?? "percent") === "fixed") return dv;
    const sub = (estimate.lineItems ?? []).reduce((s: number, li: { total: number }) => s + li.total, 0);
    const { total } = computeDocumentTotals({
      subtotal: sub,
      discountType: estimate.discountType,
      discountValue: estimate.discountValue,
      taxEnabled: estimate.taxEnabled ?? false,
      taxRate: estimate.taxRate ?? 0,
      selectedTaxLines: estimate.selectedTaxLines,
    });
    return total * (dv / 100);
  })();

  if (isLoading) {
    return <div className="space-y-6">
      <Skeleton className="h-8 w-1/3" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Skeleton className="h-[400px] lg:col-span-2" />
        <Skeleton className="h-[400px]" />
      </div>
    </div>;
  }

  if (!estimate) return <div className="text-center py-12">Quote not found</div>;

  return (
    <div className="space-y-6 animate-in fade-in duration-300 pb-24">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <Link href="/quotes">
          <Button variant="ghost" size="sm" className="gap-1.5 -ml-2 text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-4 h-4" />
            Back to Quotes
          </Button>
        </Link>
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-1.5 mb-1 group/num">
              <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Quote #</span>
              <InlineEditField
                value={estimate.estimateNumber || ""}
                onSave={(v) => saveEstimateField({ estimateNumber: v })}
                placeholder="e.g. 26-15"
                emptyPlaceholder="Add number"
                displayClassName="text-xs font-semibold uppercase tracking-widest text-muted-foreground"
                inputClassName="h-6 text-xs px-1.5 py-0 w-28"
              />
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <InlineEditField
                value={estimate.title}
                onSave={(v) => saveEstimateField({ title: v })}
                required
                requiredMessage="Quote title is required."
                onValidationError={(msg) => toast({ title: "Validation error", description: msg, variant: "destructive" })}
                placeholder="Quote title"
                displayClassName="text-3xl font-bold tracking-tight text-foreground"
                inputClassName="text-2xl font-bold h-10"
                className="flex-1"
              />
              <Badge variant="outline" className={cn(
                "px-2.5 py-0.5 text-xs font-semibold",
                estimate.status === "accepted" && "bg-green-100 text-green-800 border-green-300",
                estimate.status === "sent" && "bg-blue-100 text-blue-800 border-blue-300",
                estimate.status === "declined" && "bg-red-100 text-red-800 border-red-300",
                estimate.status === "converted" && "bg-purple-100 text-purple-800 border-purple-300",
                estimate.status === "draft" && "bg-slate-100 text-slate-700 border-slate-300"
              )}>
                {estimate.status === "converted" ? "Converted to Invoice" : estimate.status.charAt(0).toUpperCase() + estimate.status.slice(1)}
              </Badge>
              {estimate.isRecurring && (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-violet-50 text-violet-700 border border-violet-200 rounded-full font-medium">
                  <RefreshCw className="w-3 h-3" />
                  Recurring · {
                    RECURRING_INTERVAL_OPTIONS.find(o => o.value === normalizeInterval(estimate.recurringInterval))?.label
                    ?? estimate.recurringInterval ?? ""
                  }
                </span>
              )}
            </div>
            <p className="text-muted-foreground mt-1 flex items-center gap-2 text-sm flex-wrap">
              <InlineCustomerSelect
                customerName={estimate.customerName}
                customerId={estimate.customerId}
                customers={customers}
                open={inlineCustomerOpen}
                search={inlineCustomerSearch}
                onOpenChange={setInlineCustomerOpen}
                onSearchChange={setInlineCustomerSearch}
                onSelect={handleInlineCustomerChange}
                onCreateNew={(name) => {
                  setPendingCustomerName(name);
                  setCreateCustomerModalOpen(true);
                  setInlineCustomerOpen(false);
                }}
              />
              <span className="text-muted-foreground/50">•</span>
              <Link href={`/customers/${estimate.customerId}`} className="hover:text-primary hover:underline text-xs">
                View profile
              </Link>
              <span className="text-muted-foreground/50">•</span>
              Created {formatDate(estimate.createdAt)}
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-1">
              <InlineEditDate
                value={estimate.estimateDate || ""}
                onSave={(v) => saveEstimateField({ estimateDate: v })}
                label="Quote date"
                formatDate={formatDate}
                emptyPlaceholder="Set date"
              />
              <span className="text-muted-foreground/50 text-xs">•</span>
              <InlineEditDate
                value={estimate.validUntil || ""}
                onSave={(v) => saveEstimateField({ validUntil: v })}
                label="Valid until"
                formatDate={formatDate}
                emptyPlaceholder="Set date"
              />
            </div>
            <div style={{ marginTop: "0.5rem" }} data-tour-target="quote-project-select">
              <ProjectAssignment kind="quote" id={estimateId} />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2" data-tour-target="quote-actions">
          <QuickBooksPushButton kind="estimate" id={estimateId} />
          <XeroPushButton kind="estimate" id={estimateId} />
          <EditorActionBar
            tourTargets={{ previewButton: "quote-preview-btn", statusButton: "quote-status-btn", sendEmailButton: "quote-send-btn" }}
            statusOptions={[
              { label: "Mark as Draft", value: "draft", description: "Still being worked on — not yet sent to the customer." },
              { label: "Mark as Sent", value: "sent", description: "Marks it delivered and awaiting the customer's decision." },
              { label: "Mark as Accepted", value: "accepted", description: "Customer approved it — ready to convert to an invoice." },
              { label: "Mark as Declined", value: "declined", description: "Customer turned it down. Kept for your records." },
            ]}
            currentStatus={estimate.status}
            onStatusChange={onStatusChange}
            previewOptions={[
              {
                label: "Customer PDF",
                description: "Line items only, no private data",
                icon: <FileText className="w-4 h-4 mr-2 text-blue-500" />,
                onClick: () => {
                  setPreviewBlobUrl(null);
                  setPreviewTitle("Customer PDF");
                  setPreviewOpen(true);
                  // Defer generation so the dialog renders first (avoids UI freeze)
                  setTimeout(() => {
                    const url = previewCustomerPDF(estimateForPdf(), companySettings ?? {});
                    setPreviewBlobUrl(url);
                  }, 50);
                },
              },
              {
                label: isPro ? "Cost Report PDF" : "Cost Report PDF (Pro)",
                description: "Full details, costs, notes",
                icon: <Lock className="w-4 h-4 mr-2 text-primary" />,
                onClick: () => {
                  if (!isPro) { setLocation("/upgrade"); return; }
                  setPreviewBlobUrl(null);
                  setPreviewTitle("Cost Report PDF");
                  setPreviewOpen(true);
                  setTimeout(() => {
                    const url = previewInternalPDF(estimate, companySettings ?? {});
                    setPreviewBlobUrl(url);
                  }, 50);
                },
              },
            ]}
            sendEmailFeature="EMAIL_SENDING"
            onSendEmail={() => {
              const applyVars = (template: string, vars: Record<string, string>) =>
                template.replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? `{${k}}`);
              const vars = {
                customerName: estimate.customerName || "there",
                quoteNumber: estimate.estimateNumber || "",
                invoiceNumber: "",
                companyName: companySettings?.companyName || "us",
              };
              const defaultSubject = `Quote ${estimate.estimateNumber ? `#${estimate.estimateNumber}` : ""} from ${companySettings?.companyName || "us"}`;
              const defaultBody = `Hi ${estimate.customerName || "there"},\n\nPlease find your quote attached. Let us know if you have any questions.\n\nThank you for your business!`;
              setEmailSubject(companySettings?.defaultQuoteEmailSubject ? applyVars(companySettings.defaultQuoteEmailSubject, vars) : defaultSubject);
              setEmailMessage(companySettings?.defaultQuoteEmailBody ? applyVars(companySettings.defaultQuoteEmailBody, vars) : defaultBody);
              setSendEmailOpen(true);
            }}
            exportOptions={[
              {
                label: "Customer PDF",
                description: "Line items only, no private data",
                icon: <FileText className="w-4 h-4 mr-2 text-blue-500" />,
                onClick: () => {
                  exportCustomerPDF(estimateForPdf(), companySettings ?? {});
                  if (!["sent", "accepted", "declined"].includes(estimate.status)) {
                    setMarkAsSentOpen(true);
                  }
                },
              },
              {
                label: isPro ? "Cost Report PDF" : "Cost Report PDF (Pro)",
                description: "Full details, costs, notes",
                icon: <Lock className="w-4 h-4 mr-2 text-primary" />,
                onClick: () => {
                  if (!isPro) { setLocation("/upgrade"); return; }
                  exportInternalPDF(estimate, companySettings ?? {});
                },
              },
            ]}
            onDuplicate={onDuplicate}
            isDuplicating={duplicateEstimate.isPending}
            duplicateLabel="Duplicate"
            onDelete={() => setIsDeleteOpen(true)}
            deleteLabel="Delete"
            onSaveAsTemplate={() => {
              setSaveAsTemplateName(estimate?.title || "");
              setSaveAsTemplateDesc("");
              setSaveAsTemplateOpen(true);
            }}
            convertDesktopSlot={
              estimate?.status === "converted" && estimate?.linkedInvoiceId ? (
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <Button size="icon" className="h-10 w-10 text-green-700 dark:text-green-500" variant="outline" aria-label="View Invoice" asChild>
                      <Link href={`/invoices/${estimate.linkedInvoiceId}`} className="flex items-center">
                        <Receipt className="w-5 h-5" strokeWidth={2.25} />
                      </Link>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>View linked invoice</TooltipContent>
                </Tooltip>
              ) : (
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <Button
                      size="icon"
                      className="h-10 w-10 text-green-700 dark:text-green-500"
                      variant="outline"
                      aria-label="Convert to Invoice"
                      onClick={onConvertToInvoice}
                      disabled={convertToInvoice.isPending}
                      data-tour-target="quote-convert-btn"
                    >
                      {convertToInvoice.isPending ? (
                        <Loader2 className="w-5 h-5 animate-spin" strokeWidth={2.25} />
                      ) : (
                        <Receipt className="w-5 h-5" strokeWidth={2.25} />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs text-pretty leading-snug">Creates a new invoice from this estimate's line items and marks the estimate as converted. The estimate stays for your records.</TooltipContent>
                </Tooltip>
              )
            }
          />
          </div>
        </div>
      </div>

      <hr className="border-border" />

      {/* Mobile-only: Internal Workshop card shown above the grid.
          The `!` modifier emits `display:none !important` so this instance
          is reliably hidden at >=768px even if a third-party stylesheet
          tries to override the display utility. */}
      <InternalWorkshopCard
        estimate={estimate}
        formatCurrency={formatCurrency}
        className="md:hidden!"
        onTargetChange={saveTargetMargin}
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
        
        {/* Left Column */}
        <div className="md:col-span-2 space-y-6">
          <Card data-walkthrough-target="line-items-section" className="shadow-sm border-border/50">
            <CardHeader className="flex flex-row items-center justify-between pb-2 border-b gap-3 bg-muted/40">
              <div className="shrink-0">
                <CardTitle className="text-lg">Line Items</CardTitle>
                <CardDescription>Customer-facing pricing and scope</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8 text-xs gap-1">
                      <Plus className="w-3.5 h-3.5" />
                      Add
                      <ChevronDown className="w-3 h-3 opacity-60" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44">
                    <DropdownMenuItem onClick={() => setAddLineItemOpen(true)}>
                      <Plus className="w-3.5 h-3.5 mr-2" />
                      Add Line Item
                    </DropdownMenuItem>
                    {isPro ? (
                      <DropdownMenuItem onClick={handleAddGroup} disabled={createGroup.isPending}>
                        {createGroup.isPending ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-2" />}
                        Add Group
                      </DropdownMenuItem>
                    ) : (
                      <Tooltip delayDuration={0}>
                        <TooltipTrigger asChild>
                          <DropdownMenuItem asChild>
                            <Link href="/upgrade" className="flex items-center w-full text-amber-700 dark:text-amber-400 focus:text-amber-700 dark:focus:text-amber-400">
                              <Plus className="w-3.5 h-3.5 mr-2 opacity-60" />
                              <span className="opacity-60">Add Group</span>
                              <Lock className="w-3.5 h-3.5 ml-auto" />
                            </Link>
                          </DropdownMenuItem>
                        </TooltipTrigger>
                        <TooltipContent>Upgrade to Pro to unlock {PRO_FEATURES.LINE_ITEM_GROUPS}</TooltipContent>
                      </Tooltip>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardHeader>
            {addLineItemOpen && (
              <AddItemSearch estimateId={estimateId} onClose={() => setAddLineItemOpen(false)} />
            )}
            {addingToGroupId !== null && (
              <AddItemSearch estimateId={estimateId} groupId={addingToGroupId} onClose={() => setAddingToGroupId(null)} />
            )}
            <CardContent className="p-0">
              <div className="border-b">
                  {(() => {
                    const sortedGroups = [...(estimate.groups ?? [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
                    const sortedItems = [...estimate.lineItems].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
                    const ungroupedItems = sortedItems.filter(li => !li.groupId);
                    return (
                      <>
                        <div className="divide-y divide-gray-300">
                          {ungroupedItems.map((item) => (
                            <LineItemRow
                              key={item.id}
                              item={item}
                              estimateId={estimateId}
                              allItems={sortedItems}
                              index={sortedItems.findIndex(i => i.id === item.id)}
                              groups={sortedGroups}
                            />
                          ))}
                          <button
                            type="button"
                            data-tour-target="quote-add-line-item"
                            onClick={() => setAddLineItemOpen(true)}
                            className="w-full flex items-center justify-center gap-2 py-3.5 text-sm text-muted-foreground hover:text-primary hover:bg-muted/30 border-t border-dashed border-border/50 transition-colors"
                          >
                            <Plus className="w-4 h-4" />
                            Add line item
                          </button>
                        </div>
                        {sortedGroups.map((group) => {
                          const groupItems = sortedItems.filter(li => li.groupId === group.id);
                          return (
                            <GroupHeader
                              key={group.id}
                              group={group}
                              estimateId={estimateId}
                              items={groupItems}
                              allGroups={sortedGroups}
                            >
                              <div className="divide-y divide-gray-300">
                                {groupItems.map((item) => (
                                  <LineItemRow
                                    key={item.id}
                                    item={item}
                                    estimateId={estimateId}
                                    allItems={sortedItems}
                                    index={sortedItems.findIndex(i => i.id === item.id)}
                                    groups={sortedGroups}
                                  />
                                ))}
                                <button
                                  type="button"
                                  onClick={() => setAddingToGroupId(group.id)}
                                  className="w-full flex items-center justify-center gap-2 py-2.5 text-sm text-muted-foreground hover:text-primary hover:bg-muted/30 border-dashed border-t border-border/50 transition-colors"
                                >
                                  <Plus className="w-3.5 h-3.5" />
                                  Add line item
                                </button>
                              </div>
                            </GroupHeader>
                          );
                        })}
                      </>
                    );
                  })()}
                </div>
              <PricingSummary estimate={estimate} estimateId={estimateId} formatCurrency={formatCurrency} companySettings={companySettings} />
            </CardContent>
          </Card>

          <Card className="shadow-sm border-border/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Project Description</CardTitle>
            </CardHeader>
            <CardContent>
              <InlineEditField
                value={estimate.description || ""}
                onSave={(v) => saveEstimateField({ description: v })}
                multiline
                placeholder="Add a project description..."
                emptyPlaceholder="Click to add a description..."
                displayClassName="whitespace-pre-wrap text-sm"
                inputClassName="text-sm"
              />
            </CardContent>
          </Card>

          <Card className="shadow-sm border-border/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Notes / Terms</CardTitle>
            </CardHeader>
            <CardContent>
              <InlineEditField
                value={estimate.termsAndNotes || ""}
                onSave={(v) => saveEstimateField({ termsAndNotes: v || null })}
                multiline
                placeholder="Add notes or terms for this quote..."
                emptyPlaceholder="Click to add notes or terms..."
                displayClassName="whitespace-pre-wrap text-sm"
                inputClassName="text-sm"
              />
            </CardContent>
          </Card>

          <ProGate feature="FILE_ATTACHMENTS">
            <AttachmentsPanel estimateId={estimateId} />
          </ProGate>
        </div>

        {/* Right Column: Internal Workshop Summary + Deposit */}
        <div className="md:col-span-1 space-y-4">
          {/* Desktop-only: Internal Workshop card in sidebar.
              The `!` modifier emits `display:block !important` so this
              instance reliably appears at >=768px and cannot be hidden
              by a stale cached stylesheet or a third-party rule with
              equal specificity. */}
          <div data-tour-target="quote-internal-workshop">
            <InternalWorkshopCard
              estimate={estimate}
              formatCurrency={formatCurrency}
              className="hidden md:block!"
              onTargetChange={saveTargetMargin}
            />
          </div>

          <div data-tour-target="quote-deposit">
            <EstimatePaymentPlanSection
              estimate={estimate}
              estimateId={estimateId}
              formatCurrency={formatCurrency}
              milestones={estimateMilestones ?? []}
              isRecurringActive={!!estimate.isRecurring}
            />
          </div>
          <ProGate feature={GATE_RECURRING} mode="overlay">
            <RecurringPanel
              estimate={estimate}
              estimateId={estimateId}
              isMilestoneEstimate={(estimateMilestones?.length ?? 0) > 0}
              isPaymentPlanActive={(estimate.depositValue ?? 0) > 0 || (estimateMilestones?.length ?? 0) > 0}
            />
          </ProGate>
        </div>
      </div>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Quote?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this quote and all associated items and notes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); onDelete(); }} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={convertConfirmOpen} onOpenChange={setConvertConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Quote Not Yet Accepted</AlertDialogTitle>
            <AlertDialogDescription>
              This quote has not been accepted yet. Are you sure you want to convert it to an invoice anyway?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); setConvertConfirmOpen(false); doConvertToInvoice(); }}>
              Convert Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PDFPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        blobUrl={previewBlobUrl}
        title={previewTitle}
      />

      <MarkAsSentDialog
        open={markAsSentOpen}
        estimateCount={1}
        onConfirm={() => {
          setMarkAsSentOpen(false);
          updateEstimate.mutate({ id: estimateId, data: { status: "sent" } }, {
            onSuccess: () => {
              queryClient.invalidateQueries({ queryKey: getGetEstimateQueryKey(estimateId) });
              queryClient.invalidateQueries({ queryKey: getListEstimatesQueryKey() });
              toast({ title: "Quote marked as Sent" });
            }
          });
        }}
        onSkip={() => setMarkAsSentOpen(false)}
      />

      <DepositInvoicePromptDialog
        open={depositPromptOpen}
        depositAmount={formatCurrency(depositAmountForDialog)}
        isCreating={convertToInvoice.isPending || createDepositInvoice.isPending}
        onConfirm={handleDepositInvoiceConfirm}
        onSkip={() => setDepositPromptOpen(false)}
      />

      <Dialog open={sendEmailOpen} onOpenChange={(open) => { setSendEmailOpen(open); if (!open) { setCcSelf(true); setAttachServiceAgreement(true); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-4 h-4" /> Send Quote via Email
            </DialogTitle>
            <DialogDescription>
              {estimate?.customerEmail
                ? <>Sending to <span className="font-medium text-foreground">{estimate.customerEmail}</span></>
                : <span className="text-destructive">No email on file for this customer. Please add one to their record first.</span>
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-sm font-medium block mb-1">Subject</label>
              <Input value={emailSubject} onChange={e => setEmailSubject(e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Message</label>
              <Textarea
                value={emailMessage}
                onChange={e => setEmailMessage(e.target.value)}
                className="min-h-[120px] resize-none text-sm"
              />
            </div>
            {companySettings?.replyToEmail ? (
              <div className="flex items-center gap-2">
                <input
                  id="cc-self-estimate-detail"
                  type="checkbox"
                  checked={ccSelf}
                  onChange={e => setCcSelf(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 accent-primary cursor-pointer"
                />
                <label htmlFor="cc-self-estimate-detail" className="text-sm cursor-pointer select-none">
                  Send a copy to myself <span className="text-muted-foreground">({companySettings.replyToEmail})</span>
                </label>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                To CC yourself on sent quotes, add a reply-to email in{" "}
                <a href="/settings" className="underline hover:text-foreground transition-colors">Settings</a>.
              </p>
            )}
            {companySettings?.serviceAgreementFileName && (
              <div className="flex items-center gap-2">
                <input
                  id="attach-service-agreement-detail"
                  type="checkbox"
                  checked={attachServiceAgreement}
                  onChange={e => setAttachServiceAgreement(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 accent-primary cursor-pointer"
                />
                <label htmlFor="attach-service-agreement-detail" className="text-sm cursor-pointer select-none">
                  Attach Service Agreement <span className="text-muted-foreground">({companySettings.serviceAgreementFileName})</span>
                </label>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setSendEmailOpen(false); setCcSelf(true); setAttachServiceAgreement(true); }}>Cancel</Button>
            <Button
              disabled={!estimate?.customerEmail || sendEmailMutation.isPending || isGeneratingEmailPdf || !emailSubject || !emailMessage}
              onClick={() => {
                if (!estimate?.customerEmail) return;
                const attachmentFilename = customerPDFFilename(estimate);
                // Defer heavy synchronous PDF generation so the "Generating…" label
                // renders before the main thread blocks.
                setIsGeneratingEmailPdf(true);
                setTimeout(() => {
                  const attachmentBase64 = customerPDFBase64(estimateForPdf(), companySettings ?? {});
                  setIsGeneratingEmailPdf(false);
                  sendEmailMutation.mutate(
                    { id: estimateId, body: { subject: emailSubject, message: emailMessage, attachmentBase64, attachmentFilename, ccSelf, attachServiceAgreement } },
                    {
                      onSuccess: () => {
                        setSendEmailOpen(false);
                        setCcSelf(true);
                        setAttachServiceAgreement(true);
                        queryClient.invalidateQueries({ queryKey: getGetEstimateQueryKey(estimateId) });
                        queryClient.invalidateQueries({ queryKey: getListEstimatesQueryKey() });
                        toast({ title: "Email sent", description: `Quote sent to ${estimate.customerEmail}` });
                        emitTourEvent("quote.sent");
                      },
                      onError: (err: unknown) => {
                        const msg = err instanceof Error ? err.message : "Failed to send email";
                        toast({ title: "Failed to send", description: msg, variant: "destructive" });
                      }
                    }
                  );
                }, 50);
              }}
            >
              {(isGeneratingEmailPdf || sendEmailMutation.isPending) ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {isGeneratingEmailPdf ? "Generating…" : "Sending..."}</> : <><Mail className="w-4 h-4 mr-2" /> Send Email</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={saveAsTemplateOpen} onOpenChange={(o) => { if (!isSavingTemplate) setSaveAsTemplateOpen(o); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Save as Template
            </DialogTitle>
            <DialogDescription>
              Save this quote as a reusable template. Line items, costs, tax, deposit, and recurring settings will be captured.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-sm font-medium block mb-1">Template Name</label>
              <Input
                value={saveAsTemplateName}
                onChange={e => setSaveAsTemplateName(e.target.value)}
                placeholder="e.g. Full Bathroom Renovation"
              />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Description <span className="text-muted-foreground font-normal">(optional)</span></label>
              <Input
                value={saveAsTemplateDesc}
                onChange={e => setSaveAsTemplateDesc(e.target.value)}
                placeholder="Brief description of when to use this template"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={isSavingTemplate} onClick={() => setSaveAsTemplateOpen(false)}>Cancel</Button>
            <Button
              disabled={!saveAsTemplateName.trim() || isSavingTemplate}
              onClick={async () => {
                setIsSavingTemplate(true);
                try {
                  const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
                  const res = await fetch(`${BASE}/api/estimates/${estimateId}/save-as-template`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify({ name: saveAsTemplateName.trim(), description: saveAsTemplateDesc.trim() || null }),
                  });
                  if (!res.ok) throw new Error("Failed");
                  setSaveAsTemplateOpen(false);
                  toast({
                    title: "Template saved",
                    description: (
                      <span>
                        "{saveAsTemplateName.trim()}" saved.{" "}
                        <a href="/templates" className="underline font-medium">View Templates →</a>
                      </span>
                    ),
                  });
                } catch {
                  toast({ title: "Failed to save template", variant: "destructive" });
                } finally {
                  setIsSavingTemplate(false);
                }
              }}
            >
              {isSavingTemplate ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : "Save Template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CreateCustomerModal
        open={createCustomerModalOpen}
        initialName={pendingCustomerName}
        isSubmitting={createCustomer.isPending}
        onOpenChange={setCreateCustomerModalOpen}
        onSubmit={handleCreateCustomerFromInline}
      />

      <EditorDisclaimer />
    </div>
  );
}

// ─── Pricing Summary (Subtotal / Tax / Total) ─────────────────────────────────

type TaxLineEntry = TaxLine;

function parseSettingsTaxLines(raw: string | null | undefined): TaxLineEntry[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((t: any) => t.name && typeof t.rate === "number");
  } catch { return []; }
}

function PricingSummary({ estimate, estimateId, formatCurrency, companySettings }: {
  estimate: EstimateDetail;
  estimateId: number;
  formatCurrency: (v: number) => string;
  companySettings?: { taxLines?: string | null } | null;
}) {
  const queryClient = useQueryClient();
  const updateEstimate = useUpdateEstimate();
  const { toast } = useToast();

  const taxEnabled: boolean = estimate.taxEnabled ?? false;
  const subtotal: number = estimate.lineItems.reduce((s, li) => s + li.total, 0);

  const settingsTaxLines = parseSettingsTaxLines(companySettings?.taxLines);
  const [localTaxEnabled, setLocalTaxEnabled] = useState(taxEnabled);
  const [localSelectedLines, setLocalSelectedLines] = useState<TaxLineEntry[]>(
    parseSelectedTaxLines(estimate.selectedTaxLines)
  );

  const initialDiscountEnabled = (estimate.discountValue ?? 0) > 0 && !!estimate.discountType;
  const [discountEnabled, setDiscountEnabled] = useState(initialDiscountEnabled);
  const [discountType, setDiscountType] = useState<"percent" | "fixed">(
    (estimate.discountType as "percent" | "fixed" | null) ?? "percent"
  );
  const [discountInput, setDiscountInput] = useState<string>(
    estimate.discountValue != null ? String(estimate.discountValue) : ""
  );

  useEffect(() => {
    setLocalTaxEnabled(estimate.taxEnabled ?? false);
    setLocalSelectedLines(parseSelectedTaxLines(estimate.selectedTaxLines));
  }, [estimate.taxEnabled, estimate.selectedTaxLines]);

  useEffect(() => {
    const enabled = (estimate.discountValue ?? 0) > 0 && !!estimate.discountType;
    setDiscountEnabled(enabled);
    setDiscountType((estimate.discountType as "percent" | "fixed" | null) ?? "percent");
    setDiscountInput(estimate.discountValue != null ? String(estimate.discountValue) : "");
  }, [estimate.discountType, estimate.discountValue]);

  const parsedDiscountValue = (() => {
    const n = Number(discountInput);
    if (!Number.isFinite(n) || n < 0) return 0;
    if (discountType === "percent") return Math.min(n, 100);
    return n;
  })();
  const previewDiscountAmount = discountEnabled && parsedDiscountValue > 0
    ? Math.max(0, Math.min(
        discountType === "percent" ? subtotal * (parsedDiscountValue / 100) : parsedDiscountValue,
        subtotal,
      ))
    : 0;
  const taxableBase = subtotal - previewDiscountAmount;

  const legacyTaxRate: number = estimate.taxRate ?? 0;
  // Registration numbers come from Settings, not the stored snapshot, so a
  // corrected tax ID shows up on existing quotes instead of the old number.
  const activeTaxLines = reconcileTaxLineIds(localSelectedLines, companySettings?.taxLines);
  const previewTaxAmount = localTaxEnabled
    ? (activeTaxLines.length > 0
        ? activeTaxLines.reduce((sum, tl) => sum + taxableBase * (tl.rate / 100), 0)
        : taxableBase * (legacyTaxRate / 100))
    : 0;
  const previewTotal = taxableBase + previewTaxAmount;

  const saveToServer = (enabled: boolean, lines: TaxLineEntry[]) => {
    updateEstimate.mutate(
      { id: estimateId, data: { taxEnabled: enabled, selectedTaxLines: JSON.stringify(lines) } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetEstimateQueryKey(estimateId) });
          toast({ title: "Tax settings saved" });
        },
        onError: () => {
          toast({ title: "Failed to save tax settings", variant: "destructive" });
        },
      }
    );
  };

  const saveDiscount = (type: "percent" | "fixed" | null, value: number | null) => {
    updateEstimate.mutate(
      { id: estimateId, data: { discountType: type, discountValue: value } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetEstimateQueryKey(estimateId) });
          toast({ title: "Discount saved" });
        },
        onError: () => {
          toast({ title: "Failed to save discount", variant: "destructive" });
        },
      }
    );
  };

  const handleDiscountToggle = () => {
    const next = !discountEnabled;
    setDiscountEnabled(next);
    if (!next) {
      saveDiscount(null, null);
    } else if (parsedDiscountValue > 0) {
      saveDiscount(discountType, parsedDiscountValue);
    }
  };

  const handleDiscountTypeChange = (next: "percent" | "fixed") => {
    setDiscountType(next);
    if (discountEnabled && parsedDiscountValue > 0) {
      const clamped = next === "percent" ? Math.min(parsedDiscountValue, 100) : parsedDiscountValue;
      setDiscountInput(String(clamped));
      saveDiscount(next, clamped);
    }
  };

  const commitDiscountValue = () => {
    if (!discountEnabled) return;
    const n = Number(discountInput);
    if (!Number.isFinite(n) || n < 0) {
      saveDiscount(null, null);
      setDiscountInput("");
      return;
    }
    const clamped = discountType === "percent" ? Math.min(n, 100) : n;
    if (clamped !== n) setDiscountInput(String(clamped));
    if (clamped <= 0) {
      saveDiscount(null, null);
    } else {
      saveDiscount(discountType, clamped);
    }
  };

  const handleToggle = () => {
    const next = !localTaxEnabled;
    setLocalTaxEnabled(next);
    saveToServer(next, localSelectedLines);
  };

  const handleLineToggle = (line: TaxLineEntry) => {
    const isSelected = localSelectedLines.some(l => l.name === line.name && l.rate === line.rate);
    const next = isSelected
      ? localSelectedLines.filter(l => !(l.name === line.name && l.rate === line.rate))
      : [...localSelectedLines, line];
    setLocalSelectedLines(next);
    saveToServer(localTaxEnabled, next);
  };

  const showBreakdown = localTaxEnabled || previewDiscountAmount > 0;

  return (
    <div className="p-4 bg-muted/10 border-t space-y-2">
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={handleDiscountToggle}
          disabled={updateEstimate.isPending}
          className={cn(
            "relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus-visible:outline-none",
            discountEnabled ? "bg-primary" : "bg-input"
          )}
          aria-label="Toggle discount"
        >
          <span className={cn(
            "inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform",
            discountEnabled ? "translate-x-4" : "translate-x-0.5"
          )} />
        </button>
        <span className="text-sm font-medium text-muted-foreground">Apply Discount</span>
      </div>

      {discountEnabled && (
        <div className="mb-3 flex items-center gap-2">
          <div className="inline-flex rounded-md border border-input overflow-hidden">
            <button
              type="button"
              onClick={() => handleDiscountTypeChange("percent")}
              className={cn(
                "px-2 py-1 text-xs flex items-center gap-1",
                discountType === "percent" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"
              )}
              aria-label="Percent discount"
            >
              <Percent className="w-3 h-3" />
            </button>
            <button
              type="button"
              onClick={() => handleDiscountTypeChange("fixed")}
              className={cn(
                "px-2 py-1 text-xs flex items-center gap-1 border-l border-input",
                discountType === "fixed" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"
              )}
              aria-label="Fixed discount"
            >
              <DollarSign className="w-3 h-3" />
            </button>
          </div>
          <input
            type="number"
            min={0}
            max={discountType === "percent" ? 100 : undefined}
            step="0.01"
            value={discountInput}
            onChange={(e) => setDiscountInput(e.target.value)}
            onBlur={commitDiscountValue}
            onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
            placeholder={discountType === "percent" ? "0–100" : "0.00"}
            className="w-24 px-2 py-1 text-sm border border-input rounded-md bg-background"
          />
          <span className="text-xs text-muted-foreground">
            {discountType === "percent" ? "% off" : `${formatCurrency(0).replace(/[\d.,\s]/g, "") || "$"} off`}
          </span>
        </div>
      )}

      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={handleToggle}
          disabled={updateEstimate.isPending}
          className={cn(
            "relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus-visible:outline-none",
            localTaxEnabled ? "bg-primary" : "bg-input"
          )}
          aria-label="Toggle tax"
        >
          <span className={cn(
            "inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform",
            localTaxEnabled ? "translate-x-4" : "translate-x-0.5"
          )} />
        </button>
        <span className="text-sm font-medium text-muted-foreground">Charge Tax</span>
        {updateEstimate.isPending && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
      </div>

      {localTaxEnabled && (
        <div className="mb-3">
          {settingsTaxLines.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">
              No tax lines configured.{" "}
              <a href="/settings" className="underline text-primary">Go to Settings</a>{" "}
              to add tax lines.
            </p>
          ) : (
            <div className="space-y-1.5">
              {settingsTaxLines.map((line, idx) => {
                const isChecked = localSelectedLines.some(l => l.name === line.name && l.rate === line.rate);
                return (
                  <label key={idx} className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => handleLineToggle(line)}
                      disabled={updateEstimate.isPending}
                      className="rounded border-input"
                    />
                    <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                      {line.name} ({line.rate}%){line.taxId ? ` — ${line.taxId}` : ""}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      )}

      {showBreakdown ? (
        <div className="space-y-1.5">
          <div className="flex justify-between items-center text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1">Subtotal <InfoTip content={FORMULAS.subtotal} /></span>
            <span>{formatCurrency(subtotal)}</span>
          </div>
          {previewDiscountAmount > 0 && (
            <div className="flex justify-between items-center text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                Discount{discountType === "percent" ? ` (${parsedDiscountValue}%)` : ""} <InfoTip content={FORMULAS.discount} />
              </span>
              <span>-{formatCurrency(previewDiscountAmount)}</span>
            </div>
          )}
          {localTaxEnabled && (activeTaxLines.length > 0 ? (
            activeTaxLines.map((tl, idx) => (
              <div key={idx} className="flex justify-between items-center text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-1">{tl.name} ({tl.rate}%){tl.taxId ? ` — ${tl.taxId}` : ""}{idx === 0 ? <InfoTip content={FORMULAS.tax} /> : null}</span>
                <span>{formatCurrency(taxableBase * (tl.rate / 100))}</span>
              </div>
            ))
          ) : legacyTaxRate > 0 ? (
            <div className="flex justify-between items-center text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1">Tax ({legacyTaxRate}%) <InfoTip content={FORMULAS.tax} /></span>
              <span>{formatCurrency(previewTaxAmount)}</span>
            </div>
          ) : null)}
          <div className="flex justify-between items-center pt-1 border-t">
            <span className="font-semibold inline-flex items-center gap-1">Total <InfoTip content={FORMULAS.total} /></span>
            <span className="text-2xl font-bold">{formatCurrency(previewTotal)}</span>
          </div>
        </div>
      ) : (
        <div className="flex justify-between items-center">
          <span className="font-semibold text-muted-foreground">Total Customer Price</span>
          <span className="text-2xl font-bold">{formatCurrency(previewTotal)}</span>
        </div>
      )}
    </div>
  );
}

// ─── Payment Plan Section (unified Deposit + Milestones) ───────────────

function EstimatePaymentPlanSection({
  estimate,
  estimateId,
  formatCurrency,
  milestones,
  isRecurringActive = false,
}: {
  estimate: EstimateDetail;
  estimateId: number;
  formatCurrency: (v: number) => string;
  milestones: EstimateMilestoneItem[];
  isRecurringActive?: boolean;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { formatCurrency: formatCurrencyLocal } = useSettingsFormat();
  const { user } = useAuth();
  const { hasProAccess: isPro } = getTrialInfo(user);

  // ── Shared: mutation hooks for mode-switching clears ────────────────────────
  const updateEstimateForMode = useUpdateEstimate();
  const upsertMilestonesForMode = useUpsertEstimateMilestones();

  // ── Derive current mode from data ───────────────────────────────────────────
  const deriveMode = (): PaymentMode => {
    if ((milestones?.length ?? 0) > 0) return "progress";
    if ((estimate.depositValue ?? 0) > 0) return "deposit";
    return "none";
  };

  const [mode, setMode] = useState<PaymentMode>(deriveMode());
  const [isModeChangePending, setIsModeChangePending] = useState(false);

  useEffect(() => {
    setMode(deriveMode());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimate.depositValue, milestones?.length]);

  const handleModeChange = (newMode: PaymentMode) => {
    if (newMode === mode || isModeChangePending) return;
    if (newMode !== "none" && isRecurringActive) return;
    const prevMode = mode;

    const needsClearDeposit = newMode !== "deposit" && (estimate.depositValue ?? 0) > 0;
    const needsClearMilestones = newMode !== "progress" && (milestones?.length ?? 0) > 0;

    setMode(newMode);

    // Auto-seed first template when switching to progress with no persisted milestones
    if (newMode === "progress" && (milestones?.length ?? 0) === 0) {
      setMilestoneRows(
        ESTIMATE_MILESTONE_TEMPLATES[0].milestones.map(m => ({
          name: m.name,
          percentage: String(m.percentage),
          amount: "",
          usePercent: true,
        }))
      );
      setIsMilestoneDirty(true);
    }

    if (!needsClearDeposit && !needsClearMilestones) return;

    setIsModeChangePending(true);

    if (needsClearMilestones) {
      upsertMilestonesForMode.mutate(
        { estimateId, data: { milestones: [] } },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getListEstimateMilestonesQueryKey(estimateId) });
            queryClient.invalidateQueries({ queryKey: getGetEstimateQueryKey(estimateId) });
            if (needsClearDeposit) {
              updateEstimateForMode.mutate(
                { id: estimateId, data: { depositType: "percent", depositValue: 0 } },
                {
                  onSuccess: () => {
                    queryClient.invalidateQueries({ queryKey: getGetEstimateQueryKey(estimateId) });
                    setIsModeChangePending(false);
                    toast({ title: "Billing plan cleared" });
                  },
                  onError: () => {
                    setIsModeChangePending(false);
                    toast({ title: "Failed to clear deposit", variant: "destructive" });
                  },
                }
              );
            } else {
              setIsModeChangePending(false);
              toast({ title: "Billing plan cleared" });
            }
          },
          onError: () => {
            setMode(prevMode);
            setIsModeChangePending(false);
            toast({ title: "Failed to clear billing plan", variant: "destructive" });
          },
        }
      );
    } else if (needsClearDeposit) {
      updateEstimateForMode.mutate(
        { id: estimateId, data: { depositType: "percent", depositValue: 0 } },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getGetEstimateQueryKey(estimateId) });
            setIsModeChangePending(false);
          },
          onError: () => {
            setMode(prevMode);
            setIsModeChangePending(false);
            toast({ title: "Failed to clear deposit", variant: "destructive" });
          },
        }
      );
    }
  };

  // ── Deposit form state ───────────────────────────────────────────────────────
  const updateEstimateDeposit = useUpdateEstimate();
  const depositType: "fixed" | "percent" = (estimate.depositType as "fixed" | "percent") ?? "percent";
  const depositValue: number = estimate.depositValue ?? 0;

  const subtotal = estimate.lineItems.reduce((s, li) => s + li.total, 0);
  const { total: grandTotal } = computeDocumentTotals({
    subtotal,
    discountType: estimate.discountType,
    discountValue: estimate.discountValue,
    taxEnabled: estimate.taxEnabled ?? false,
    taxRate: estimate.taxRate ?? 0,
    selectedTaxLines: estimate.selectedTaxLines,
  });

  const computedDepositAmount = (() => {
    if (depositValue <= 0) return 0;
    if (depositType === "fixed") return depositValue;
    return grandTotal * (depositValue / 100);
  })();

  const [localDepositType, setLocalDepositType] = useState<"fixed" | "percent">(depositType);
  const [localDepositValue, setLocalDepositValue] = useState<string>(depositValue === 0 ? "" : String(depositValue));
  const [isDepositDirty, setIsDepositDirty] = useState(false);

  useEffect(() => {
    setLocalDepositType(estimate.depositType ?? "percent");
    setLocalDepositValue(estimate.depositValue === 0 || estimate.depositValue == null ? "" : String(estimate.depositValue));
    setIsDepositDirty(false);
  }, [estimate.depositType, estimate.depositValue]);

  const depositPreviewAmount = (() => {
    const v = parseFloat(localDepositValue) || 0;
    if (v <= 0) return 0;
    if (localDepositType === "fixed") return v;
    return grandTotal * (v / 100);
  })();

  const handleDepositSave = () => {
    const v = parseFloat(localDepositValue) || 0;
    updateEstimateDeposit.mutate(
      { id: estimateId, data: { depositType: localDepositType, depositValue: v } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetEstimateQueryKey(estimateId) });
          setIsDepositDirty(false);
          toast({ title: "Deposit saved" });
        },
        onError: () => {
          toast({ title: "Failed to save deposit", variant: "destructive" });
        },
      }
    );
  };

  // ── Milestone form state ─────────────────────────────────────────────────────
  const upsertMutation = useUpsertEstimateMilestones();
  const [milestoneRows, setMilestoneRows] = useState<EstimateMilestoneRow[]>([]);
  const [isMilestoneDirty, setIsMilestoneDirty] = useState(false);
  const dragIndex = useRef<number | null>(null);

  const { data: milestonesData, isLoading: isMilestonesLoading } = useGetEstimateMilestones(estimateId, {
    query: { queryKey: getListEstimateMilestonesQueryKey(estimateId), enabled: mode === "progress" },
  });

  useEffect(() => {
    if (milestonesData && milestonesData.length > 0 && !isMilestoneDirty) {
      setMilestoneRows(
        milestonesData.map((m: EstimateMilestoneItem) => ({
          id: m.id,
          name: m.name,
          percentage: m.percentage !== null && m.percentage !== undefined ? String(m.percentage) : "",
          amount: m.amount !== null && m.amount !== undefined ? String(m.amount) : "",
          usePercent: m.percentage !== null && m.percentage !== undefined,
        }))
      );
    }
  }, [milestonesData, isMilestoneDirty]);

  const applyMilestoneTemplate = (template: typeof ESTIMATE_MILESTONE_TEMPLATES[number]) => {
    setMilestoneRows(
      template.milestones.map(m => ({
        name: m.name,
        percentage: String(m.percentage),
        amount: "",
        usePercent: true,
      }))
    );
    setIsMilestoneDirty(true);
  };

  const addMilestoneRow = () => {
    setMilestoneRows(r => [...r, { name: "", percentage: "", amount: "", usePercent: true }]);
    setIsMilestoneDirty(true);
  };

  const updateMilestoneRow = (idx: number, field: keyof EstimateMilestoneRow, value: string | boolean) => {
    setMilestoneRows(r => r.map((row, i) => (i === idx ? { ...row, [field]: value } : row)));
    setIsMilestoneDirty(true);
  };

  const removeMilestoneRow = (idx: number) => {
    setMilestoneRows(r => r.filter((_, i) => i !== idx));
    setIsMilestoneDirty(true);
  };

  const handleMilestoneDragStart = (idx: number) => { dragIndex.current = idx; };
  const handleMilestoneDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    const from = dragIndex.current;
    if (from === null || from === idx) return;
    setMilestoneRows(r => {
      const next = [...r];
      const [moved] = next.splice(from, 1);
      next.splice(idx, 0, moved);
      return next;
    });
    dragIndex.current = idx;
    setIsMilestoneDirty(true);
  };
  const handleMilestoneDragEnd = () => { dragIndex.current = null; };

  const milestoneTotalPct = milestoneRows.reduce((s, r) => s + (r.usePercent ? (parseFloat(r.percentage) || 0) : 0), 0);
  const milestoneRoundedTotal = Math.round(milestoneTotalPct * 100) / 100;
  const milestonePctRows = milestoneRows.filter(r => r.usePercent);
  const milestoneFixedRows = milestoneRows.filter(r => !r.usePercent);
  const milestoneAllPctMode = milestonePctRows.length > 0 && milestoneFixedRows.length === 0;
  const milestoneIsComplete = milestonePctRows.length === 0 || Math.abs(milestoneRoundedTotal - 100) < 0.01;
  const milestoneIsValidPct = milestoneAllPctMode ? milestoneIsComplete : milestoneRoundedTotal <= 100;

  const handleMilestoneSave = () => {
    if (milestoneAllPctMode && !milestoneIsComplete) {
      toast({ title: `Percentages must total exactly 100% (currently ${milestoneRoundedTotal.toFixed(1)}%)`, variant: "destructive" });
      return;
    }
    if (!milestoneIsValidPct) {
      toast({ title: `Percentages sum to ${milestoneRoundedTotal}%, which exceeds 100%`, variant: "destructive" });
      return;
    }
    const emptyName = milestoneRows.findIndex(r => !r.name.trim());
    if (emptyName !== -1) {
      toast({ title: `Milestone ${emptyName + 1} needs a name`, variant: "destructive" });
      return;
    }
    const emptyValue = milestoneRows.findIndex(r => {
      if (r.usePercent) return !r.percentage || isNaN(parseFloat(r.percentage));
      return !r.amount || isNaN(parseFloat(r.amount));
    });
    if (emptyValue !== -1) {
      toast({ title: `Milestone ${emptyValue + 1} needs a ${milestoneRows[emptyValue].usePercent ? "percentage" : "fixed amount"}`, variant: "destructive" });
      return;
    }

    const payload: EstimateMilestoneInput[] = milestoneRows.map((r, i) => ({
      ...(r.id !== undefined ? { id: r.id } : {}),
      name: r.name.trim(),
      percentage: r.usePercent && r.percentage ? parseFloat(r.percentage) : null,
      amount: !r.usePercent && r.amount ? parseFloat(r.amount) : null,
      sortOrder: i,
    }));

    upsertMutation.mutate(
      { estimateId, data: { milestones: payload } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListEstimateMilestonesQueryKey(estimateId) });
          queryClient.invalidateQueries({ queryKey: getGetEstimateQueryKey(estimateId) });
          setIsMilestoneDirty(false);
          toast({ title: "Billing plan saved" });
        },
        onError: (err: unknown) => {
          const e = err as { response?: { data?: { error?: string } }; message?: string };
          toast({ title: e?.response?.data?.error || "Failed to save billing plan", variant: "destructive" });
        },
      }
    );
  };

  // ── Deposit content ──────────────────────────────────────────────────────────
  const depositContent = (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">Set a deposit for this quote. Leave blank or 0 to hide from the PDF.</p>
      <div className="flex gap-1 p-1 bg-border rounded-lg w-full">
        <button
          type="button"
          onClick={() => { setLocalDepositType("fixed"); setIsDepositDirty(true); }}
          className={cn(
            "flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-md text-xs font-medium transition-colors",
            localDepositType === "fixed" ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"
          )}
        >
          <DollarSign className="w-3 h-3" /> Fixed ($)
        </button>
        <button
          type="button"
          onClick={() => { setLocalDepositType("percent"); setIsDepositDirty(true); }}
          className={cn(
            "flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-md text-xs font-medium transition-colors",
            localDepositType === "percent" ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"
          )}
        >
          <Percent className="w-3 h-3" /> Percent (%)
        </button>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
            {localDepositType === "fixed" ? "$" : "%"}
          </span>
          <Input
            type="number"
            step={localDepositType === "fixed" ? "0.01" : "0.1"}
            min="0"
            max={localDepositType === "percent" ? "100" : undefined}
            placeholder={localDepositType === "fixed" ? "500.00" : "25"}
            value={localDepositValue}
            onChange={e => { setLocalDepositValue(e.target.value); setIsDepositDirty(true); }}
            className="pl-7 text-sm h-8"
          />
        </div>
        {localDepositType === "percent" && parseFloat(localDepositValue) > 0 && subtotal > 0 && (
          <span className="text-xs text-muted-foreground shrink-0">
            = {formatCurrency(depositPreviewAmount)}
          </span>
        )}
      </div>

      {isDepositDirty && (
        <Button size="sm" className="w-full h-7 text-xs" onClick={handleDepositSave} disabled={updateEstimateDeposit.isPending}>
          {updateEstimateDeposit.isPending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
          Save Deposit
        </Button>
      )}

      {!isDepositDirty && computedDepositAmount > 0 && (
        <p className="text-xs text-muted-foreground text-center pt-1">
          Deposit: <span className="font-semibold text-foreground">{formatCurrency(computedDepositAmount)}</span>
          {depositType === "percent" && ` (${depositValue}% of grand total)`}
        </p>
      )}
    </div>
  );

  // ── Milestone content ────────────────────────────────────────────────────────
  const milestoneContent = (
    <ProGate feature={GATE_MILESTONE} mode="overlay">
      <div className="space-y-3">
        <p className="text-xs text-muted-foreground bg-muted/40 rounded-md p-2.5">
          Define how payment is split across project stages. When this quote converts to an invoice, the billing plan carries over automatically.
        </p>

        {isMilestonesLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : (
          <>
            {milestoneRows.length === 0 && (
              <div className="space-y-1.5">
                <p className="text-xs text-muted-foreground font-medium">Quick start</p>
                <div className="flex flex-wrap gap-1.5">
                  {ESTIMATE_MILESTONE_TEMPLATES.map(t => (
                    <button
                      key={t.label}
                      type="button"
                      onClick={() => applyMilestoneTemplate(t)}
                      className="text-xs border rounded px-2 py-1 hover:bg-muted transition-colors"
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {milestoneRows.length > 0 && (
              <div className="space-y-2">
                {milestoneRows.map((row, idx) => (
                  <div
                    key={idx}
                    draggable
                    onDragStart={() => handleMilestoneDragStart(idx)}
                    onDragOver={e => handleMilestoneDragOver(e, idx)}
                    onDragEnd={handleMilestoneDragEnd}
                    className="flex items-center gap-1.5 group"
                  >
                    <GripVertical className="w-3.5 h-3.5 text-muted-foreground/40 cursor-grab shrink-0" />
                    <Input
                      value={row.name}
                      onChange={e => updateMilestoneRow(idx, "name", e.target.value)}
                      placeholder="Milestone name"
                      className="h-7 text-xs flex-1 min-w-0"
                    />
                    <div className="flex items-center gap-0.5 shrink-0">
                      <button
                        type="button"
                        onClick={() => updateMilestoneRow(idx, "usePercent", true)}
                        className={cn("p-1 rounded", row.usePercent ? "bg-primary text-primary-foreground" : "hover:bg-muted")}
                        title="Percentage"
                      >
                        <Percent className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        onClick={() => updateMilestoneRow(idx, "usePercent", false)}
                        className={cn("p-1 rounded", !row.usePercent ? "bg-primary text-primary-foreground" : "hover:bg-muted")}
                        title="Fixed amount"
                      >
                        <DollarSign className="w-3 h-3" />
                      </button>
                    </div>
                    <Input
                      type="number"
                      min="0"
                      step={row.usePercent ? "0.1" : "0.01"}
                      value={row.usePercent ? row.percentage : row.amount}
                      onChange={e => updateMilestoneRow(idx, row.usePercent ? "percentage" : "amount", e.target.value)}
                      placeholder={row.usePercent ? "%" : "$"}
                      className="h-7 text-xs w-20 shrink-0"
                    />
                    <button
                      type="button"
                      onClick={() => removeMilestoneRow(idx)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:text-destructive shrink-0"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}

                {milestonePctRows.length > 0 && (
                  <div className={cn(
                    "flex items-center justify-between text-xs px-1 pt-1 border-t",
                    milestoneAllPctMode && !milestoneIsComplete ? "text-destructive" : "text-muted-foreground"
                  )}>
                    <span>Total %</span>
                    <span className="font-semibold tabular-nums">
                      {milestoneRoundedTotal.toFixed(1)}%
                      {milestoneAllPctMode && !milestoneIsComplete && (
                        <AlertTriangle className="w-3 h-3 inline ml-1" />
                      )}
                    </span>
                  </div>
                )}

                {estimate.total > 0 && milestoneRows.length > 0 && (
                  <div className="bg-muted/40 rounded-md p-2.5 text-xs space-y-1">
                    {milestoneRows.map((r, i) => {
                      const amt = r.usePercent
                        ? Math.round(estimate.total * (parseFloat(r.percentage) || 0) / 100 * 100) / 100
                        : parseFloat(r.amount) || 0;
                      return (
                        <div key={i} className="flex justify-between">
                          <span className="text-muted-foreground truncate">{r.name || `Milestone ${i + 1}`}</span>
                          <span className="font-medium tabular-nums shrink-0 ml-2">{formatCurrencyLocal(amt)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center gap-2 pt-1">
              <button
                type="button"
                onClick={addMilestoneRow}
                className="text-xs flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                Add milestone
              </button>
              {milestoneRows.length > 0 && (
                <div className="ml-auto flex gap-1.5">
                  <div className="flex flex-wrap gap-1 items-center">
                    <span className="text-xs text-muted-foreground">Template:</span>
                    {ESTIMATE_MILESTONE_TEMPLATES.map(t => (
                      <button
                        key={t.label}
                        type="button"
                        onClick={() => applyMilestoneTemplate(t)}
                        className="text-xs border rounded px-1.5 py-0.5 hover:bg-muted transition-colors"
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {milestoneRows.length > 0 && (
              <button
                type="button"
                onClick={handleMilestoneSave}
                disabled={upsertMutation.isPending}
                className="w-full h-8 text-xs bg-primary text-primary-foreground rounded-md font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
              >
                {upsertMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Save billing plan
              </button>
            )}
          </>
        )}
      </div>
    </ProGate>
  );

  const milestoneBadge = (milestones?.length ?? 0) > 0 ? (
    <span className="ml-0.5 text-xs bg-violet-100 whitespace-nowrap text-violet-700 border border-violet-200 px-1 py-0.5 rounded font-medium">
      {milestones.length}
    </span>
  ) : undefined;

  return (
    <PaymentPlanPanel
      mode={mode}
      onModeChange={handleModeChange}
      isModeChangePending={isModeChangePending}
      depositContent={depositContent}
      progressContent={milestoneContent}
      milestoneBadge={milestoneBadge}
      isProgressGated={!isPro}
      isRecurringActive={isRecurringActive}
    />
  );
}

// ─── Recurring Panel ──────────────────────────────────────────────────────────

const RECURRING_INTERVAL_OPTIONS = [
  { value: "weekly", label: "Weekly", defaultCount: 52 },
  { value: "biweekly", label: "Bi-weekly", defaultCount: 26 },
  { value: "monthly", label: "Monthly", defaultCount: 12 },
  { value: "quarterly", label: "Quarterly", defaultCount: 4 },
  { value: "semiannually", label: "Semi-annually", defaultCount: 2 },
  { value: "annually", label: "Annually", defaultCount: 1 },
  { value: "custom", label: "Custom", defaultCount: 12 },
] as const;

function normalizeInterval(iv: string | null | undefined): string {
  if (!iv) return "monthly";
  if (iv === "first_of_month" || iv === "fifteenth_of_month" || iv === "last_of_month" || iv === "custom_months") return "monthly";
  if (iv === "custom_days") return "custom";
  return iv;
}

const ESTIMATE_MILESTONE_TEMPLATES = [
  {
    label: "30 / 40 / 30",
    milestones: [
      { name: "Deposit", percentage: 30 },
      { name: "Progress payment", percentage: 40 },
      { name: "Final payment", percentage: 30 },
    ],
  },
  {
    label: "25 × 4",
    milestones: [
      { name: "Deposit", percentage: 25 },
      { name: "Milestone 2", percentage: 25 },
      { name: "Milestone 3", percentage: 25 },
      { name: "Final payment", percentage: 25 },
    ],
  },
  {
    label: "Deposit + 2 progress + final",
    milestones: [
      { name: "Deposit", percentage: 20 },
      { name: "Rough-in complete", percentage: 35 },
      { name: "Finishing work", percentage: 30 },
      { name: "Final inspection", percentage: 15 },
    ],
  },
] as const;

type EstimateMilestoneRow = { id?: number; name: string; percentage: string; amount: string; usePercent: boolean };

function RecurringPanel({ estimate, estimateId, isMilestoneEstimate = false, isPaymentPlanActive = false }: { estimate: EstimateDetail; estimateId: number; isMilestoneEstimate?: boolean; isPaymentPlanActive?: boolean }) {
  const queryClient = useQueryClient();
  const updateEstimate = useUpdateEstimate();
  const { toast } = useToast();
  const { formatCurrency } = useSettingsFormat();

  const [isOpen, setIsOpen] = useState(true);
  const [interval, setIntervalValue] = useState(normalizeInterval(estimate.recurringInterval));
  const [startDate, setStartDate] = useState(estimate.recurringStartDate ?? "");
  const [endDate, setEndDate] = useState(estimate.recurringEndDate ?? "");
  const [startDateError, setStartDateError] = useState(false);
  const deriveMode = (isRec: boolean | null | undefined): "off" | "recurring" => {
    if (!isRec) return "off";
    return "recurring";
  };
  const [mode, setMode] = useState<"off" | "recurring">(
    deriveMode(estimate.isRecurring)
  );

  useEffect(() => {
    setMode(deriveMode(estimate.isRecurring));
    setIntervalValue(normalizeInterval(estimate.recurringInterval));
    setStartDate(estimate.recurringStartDate ?? "");
    setEndDate(estimate.recurringEndDate ?? "");
  }, [estimate.isRecurring, estimate.recurringInterval, estimate.recurringStartDate, estimate.recurringEndDate]);

  const mutateEstimate = (data: Parameters<typeof updateEstimate.mutate>[0]["data"]) => {
    updateEstimate.mutate({ id: estimateId, data }, {
      onSuccess: () => queryClient.invalidateQueries({ queryKey: getGetEstimateQueryKey(estimateId) }),
      onError: () => toast({ title: "Failed to save recurring settings", variant: "destructive" }),
    });
  };

  const save = (overrides?: Record<string, unknown>) => {
    const isRecurring = mode !== "off";
    const effectiveStartDate = (overrides?.recurringStartDate as string | null | undefined) !== undefined
      ? (overrides?.recurringStartDate as string | null)
      : (startDate || null);
    if (isRecurring && !effectiveStartDate) {
      setStartDateError(true);
      return;
    }
    setStartDateError(false);
    const base = {
      isRecurring,
      recurringInterval: interval,
      recurringCustomDays: null,
      recurringStartDate: startDate || null,
      recurringEndDate: endDate || null,
    };
    mutateEstimate({ ...base, ...overrides } as Parameters<typeof updateEstimate.mutate>[0]["data"]);
  };

  const handleModeChange = (newMode: "off" | "recurring") => {
    if (newMode !== "off" && isPaymentPlanActive) return;
    setMode(newMode);
    if (newMode !== "off" && !startDate) {
      setStartDateError(true);
      return;
    }
    setStartDateError(false);
    mutateEstimate({
      isRecurring: newMode !== "off",
      recurringInterval: interval,
      recurringCustomDays: null,
      recurringStartDate: startDate || null,
      recurringEndDate: endDate || null,
    } as Parameters<typeof updateEstimate.mutate>[0]["data"]);
  };

  const intervalLabel = RECURRING_INTERVAL_OPTIONS.find(o => o.value === interval)?.label ?? "Monthly";

  const MODES = [
    { value: "off" as const, label: "Off" },
    { value: "recurring" as const, label: "Recurring" },
  ];

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className="shadow-sm border-border/50">
        <CardHeader className="border-b p-0 bg-muted/40">
          <CollapsibleTrigger asChild>
            <button type="button" className="w-full flex items-center justify-between px-6 py-4 hover:bg-muted/30 transition-colors rounded-t-lg">
              <div className="flex items-center gap-2">
                <RefreshCw className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-semibold">Recurring</span>
                {mode === "recurring" && (
                  <span className="text-xs bg-blue-100 text-blue-700 border border-blue-200 px-1.5 py-0.5 rounded font-medium">
                    {intervalLabel}
                  </span>
                )}
              </div>
              <ChevronDown className={cn("w-4 h-4 text-muted-foreground transition-transform duration-200", isOpen && "rotate-180")} />
            </button>
          </CollapsibleTrigger>
        </CardHeader>
        <CollapsibleContent>
          <CardContent className="pt-4 space-y-3">
            <div className="flex gap-1 p-1 bg-border rounded-lg text-sm font-medium">
              {MODES.map((m) => {
                const isPaymentPlanBlocked = m.value !== "off" && isPaymentPlanActive;
                const isDisabled = isPaymentPlanBlocked;
                const isActive = mode === m.value;
                const tooltipText = isPaymentPlanBlocked
                  ? "Turn off the Payment Plan first to use Recurring."
                  : null;
                const btn = (
                  <button
                    key={m.value}
                    type="button"
                    disabled={isDisabled}
                    onClick={() => handleModeChange(m.value)}
                    className={cn(
                      "flex-1 px-3 py-1.5 rounded-md transition-colors",
                      isActive ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground",
                      isDisabled && "opacity-40 cursor-not-allowed"
                    )}
                  >
                    {m.label}
                  </button>
                );
                if (tooltipText) {
                  return (
                    <Tooltip key={m.value}>
                      <TooltipTrigger asChild>
                        <span className="flex-1">{btn}</span>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-[220px] text-xs">
                        {tooltipText}
                      </TooltipContent>
                    </Tooltip>
                  );
                }
                return btn;
              })}
            </div>

            {mode !== "off" && (
              <>
                <div className="space-y-1.5">
                  <label className="text-xs text-muted-foreground font-medium">Billing cycle</label>
                  <Select value={interval} onValueChange={(v) => {
                    setIntervalValue(v);
                    save({ recurringInterval: v, recurringCustomDays: null });
                  }}>
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RECURRING_INTERVAL_OPTIONS.map(o => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs text-muted-foreground font-medium">
                    Start date
                    <span className="text-destructive ml-0.5">*</span>
                  </label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={e => {
                      setStartDate(e.target.value);
                      if (e.target.value) setStartDateError(false);
                    }}
                    onBlur={() => save({ recurringStartDate: startDate || null })}
                    className={cn("h-8 text-sm", startDateError && "border-destructive focus-visible:ring-destructive")}
                  />
                  {startDateError && (
                    <p className="text-xs text-destructive">Start date is required</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs text-muted-foreground font-medium">End date (optional)</label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={e => setEndDate(e.target.value)}
                    onBlur={() => save({ recurringEndDate: endDate || null })}
                    className="h-8 text-sm"
                  />
                </div>

                {estimate.recurringNextDate && (
                  <p className="text-xs text-muted-foreground pt-1 border-t">
                    Next copy: <span className="font-semibold text-foreground">{estimate.recurringNextDate}</span>
                  </p>
                )}
              </>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}


